import { DvToolbarAction, DvToolbarActionEvent } from '@devinco/ui/grid';
import { DvSplitterEventsService } from '@devinco/ui/dds/splitter';
import { Injectable, inject } from '@angular/core';
import { environment } from '@env/environment';
import { ColumnType } from '@features/vt-grid/grid-options/column-types';
import { vtPresetActions } from '@features/vt-grid/presets/preset-actions';
import { ToolbarActionsService } from '@features/vt-grid/services/toolbar-actions.service';
import { DamageReportStatus } from '@shared/constants/damage-report-status';
import { Entity } from '@shared/constants/entity';
import { PdfDownloadService } from '@shared/services/pdf-download.service';
import { TranslateService } from '@ngx-translate/core';
import { ColDef, GridApi } from 'ag-grid-community';
import { Observable, catchError, of } from 'rxjs';
import { DamageReportDto } from './interfaces';

const DAMAGE_REPORT_STATUS_BG: Record<DamageReportStatus, string> = {
  [DamageReportStatus.Viewed]: 'var(--dv-bg-surface-info-active)',
  [DamageReportStatus.Processing]: 'var(--dv-bg-surface-info-active)',
  [DamageReportStatus.Closed]: 'var(--dv-bg-surface-success-active)',
  [DamageReportStatus.Submitted]: 'var(--dv-bg-surface-warning-active)',
  [DamageReportStatus.Draft]: 'var(--dv-bg-surface-disabled)',
};

@Injectable()
export class DamageReportsGridService {
  private readonly toolbarActionService = inject(ToolbarActionsService);
  private readonly splitterEvents = inject(DvSplitterEventsService);
  private readonly translate = inject(TranslateService);
  private readonly pdfDownloadService = inject(PdfDownloadService);

  getColDefs = (): ColDef<DamageReportDto>[] => [
    {
      field: 'DamageReportID',
      headerName: 'Grid.Id',
      type: [ColumnType.Id],
      initialHide: true,
    },
    {
      field: 'DamageReportStatus',
      headerName: 'DamageReportsGrid.DamageReportStatus',
      type: [ColumnType.TranslatedText],
      pinned: true,
      lockPinned: true,
      cellStyle: ({ data }) => {
        const status = data?.DamageReportStatus as
          | DamageReportStatus
          | undefined;
        const background = status ? DAMAGE_REPORT_STATUS_BG[status] : undefined;
        return background ? { 'background-color': background } : undefined;
      },
    },
    {
      field: 'IncidentDateTime',
      headerName: 'DamageReportsGrid.IncidentDateTime',
      type: [ColumnType.DateTime],
    },
    {
      field: 'RegistrationNumber',
      headerName: 'DamageReportsGrid.RegistrationNumber',
      type: [ColumnType.Text],
      valueFormatter: ({ value }) => value || '-',
    },
    {
      field: 'DriverDescription',
      headerName: 'DamageReportsGrid.DriverDescription',
      type: [ColumnType.Text],
    },
    {
      field: 'CreatedByDescription',
      headerName: 'DamageReportsGrid.CreatedByDescription',
      type: [ColumnType.Text],
    },
    {
      field: 'Mobile',
      headerName: 'DamageReportsGrid.Mobile',
      type: [ColumnType.Text],
    },
    {
      field: 'ShortDescription',
      headerName: 'DamageReportsGrid.ShortDescription',
      type: [ColumnType.Text],
    },
    {
      field: 'IncidentType',
      headerName: 'DamageReportsGrid.IncidentType',
      type: [ColumnType.TranslatedText],
    },
    {
      field: 'DamageLocation',
      headerName: 'DamageReportsGrid.DamageLocation',
      type: [ColumnType.Text],
      valueGetter: ({ data }) =>
        (data?.DamageLocation || [])
          .map(({ TranslationKey }) =>
            TranslationKey ? this.translate.instant(TranslationKey) : ''
          )
          .filter(Boolean)
          .join(', '),
    },
    {
      field: 'DamageSeverity',
      headerName: 'DamageReportsGrid.DamageSeverity',
      type: [ColumnType.TranslatedText],
    },
  ];

  getGridActions(): DvToolbarAction[] {
    return [
      vtPresetActions.reset,
      vtPresetActions.fit,
      vtPresetActions.reload,
      {
        ...vtPresetActions.excel,
        clickFn: (event: DvToolbarActionEvent) =>
          this.toolbarActionService.serverSideExcelExport(
            Entity.DamageReport,
            event.api,
            event.context
          ),
      },
      {
        icon: 'export_pdf',
        clickFn: ({ api }: DvToolbarActionEvent) => this.downloadPdf(api),
        disabled: true,
        tooltipKey: 'DamageReportsGrid.ExportPdf',
      },
      {},
      {
        icon: 'delete',
        clickFn: this.toolbarActionService.deleteSelectedRow,
        disabled: true,
        tooltipKey: 'Grid.Delete',
      },
      {
        icon: 'edit',
        clickFn: () => this.splitterEvents.isSplitterOpened$.next(true),
        disabled: true,
        tooltipKey: 'Grid.Edit',
      },
    ];
  }

  private downloadPdf(
    api: GridApi<DamageReportDto>
  ): Observable<void> | undefined {
    const damageReportId = api.getSelectedNodes()[0]?.data?.DamageReportID;
    if (!damageReportId) return;

    return this.pdfDownloadService
      .downloadPdf(
        `${environment.endpoints.restApi}/report/download/pdf/${Entity.DamageReport}/${damageReportId}`,
        `DamageReport_${damageReportId}.pdf`
      )
      .pipe(catchError(() => of(undefined)));
  }
}
